import { useMemo, useState } from "react";
import styled from "@emotion/styled";
import type { Theme } from "@emotion/react";

import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";

export type UsageMetricKey = "voice" | "notes" | "requests" | "routines";

export interface UsageMetric {
  key: UsageMetricKey;
  label: string;
  total: string;
}

export interface MonthlyUsage {
  label: string;
  values: Record<UsageMetricKey, number>;
}

export interface CurrentUsageMeter {
  allowance: number | null;
  label: string;
  unit?: string;
  used: number;
}

interface SettingsUsagePageViewProps {
  currentPeriodLabel: string;
  currentUsageMeters: CurrentUsageMeter[];
  dateRangeLabel: string;
  initialMetric?: UsageMetricKey;
  metrics: UsageMetric[];
  monthlyUsage: MonthlyUsage[];
  planName: string;
  year: number;
}

const USAGE_METRIC_PRESENTATION: Record<
  UsageMetricKey,
  { icon: MDSIconKind; accent: "blue" | "gold" | "green" | "violet" }
> = {
  voice: { icon: MDSIconKind.WaveformLines, accent: "blue" },
  notes: { icon: MDSIconKind.Document, accent: "gold" },
  requests: { icon: MDSIconKind.Message, accent: "violet" },
  routines: { icon: MDSIconKind.SparkleOutline, accent: "green" },
};

function formatMonthlyValue(metricKey: UsageMetricKey, value: number): string {
  if (metricKey !== "voice") return value.toLocaleString();

  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

export function SettingsUsagePageView({
  currentPeriodLabel,
  currentUsageMeters,
  dateRangeLabel,
  initialMetric = "voice",
  metrics,
  monthlyUsage,
  planName,
  year,
}: SettingsUsagePageViewProps) {
  const [selectedMetricKey, setSelectedMetricKey] = useState<UsageMetricKey>(initialMetric);
  const selectedMetric = metrics.find(metric => metric.key === selectedMetricKey) ?? metrics[0];

  const maximumMonthlyValue = useMemo(
    () => Math.max(...monthlyUsage.map(month => month.values[selectedMetricKey]), 1),
    [monthlyUsage, selectedMetricKey]
  );

  if (!selectedMetric) return null;

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Account"
        title="Usage"
        rightContent={<YearBadge>{year}</YearBadge>}
      />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <Introduction>
            <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
              See how you use Mem over time and what counts toward your plan.
            </MDSText>
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
              {dateRangeLabel}
            </MDSText>
          </Introduction>

          <MetricGrid aria-label="Year-to-date usage metrics">
            {metrics.map(metric => {
              const presentation = USAGE_METRIC_PRESENTATION[metric.key];
              const isSelected = metric.key === selectedMetric.key;

              return (
                <MetricCard
                  key={metric.key}
                  type="button"
                  aria-pressed={isSelected}
                  $accent={presentation.accent}
                  $isSelected={isSelected}
                  onClick={() => setSelectedMetricKey(metric.key)}
                >
                  <MetricCardTopRow>
                    <MetricIcon $accent={presentation.accent}>
                      <MDSIcon kind={presentation.icon} color={MDSIconColor.Inherit} size={15} />
                    </MetricIcon>
                    <PeriodPill>YTD</PeriodPill>
                  </MetricCardTopRow>
                  <MetricValue>{metric.total}</MetricValue>
                  <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                    {metric.label}
                  </MDSText>
                </MetricCard>
              );
            })}
          </MetricGrid>

          <ChartCard>
            <ChartHeader>
              <div>
                <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                  MONTHLY PATTERN
                </MDSText>
                <ChartTitleRow>
                  <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
                    {selectedMetric.label}
                  </MDSText>
                  <ChartTotal>{selectedMetric.total} YTD</ChartTotal>
                </ChartTitleRow>
              </div>
              <ChartKey>
                <ChartKeyDot $accent={USAGE_METRIC_PRESENTATION[selectedMetric.key].accent} />
                Monthly usage
              </ChartKey>
            </ChartHeader>

            <ChartGrid $columnCount={monthlyUsage.length}>
              <GridLine $position={0} />
              <GridLine $position={1} />
              <GridLine $position={2} />
              {monthlyUsage.map((month, index) => {
                const value = month.values[selectedMetric.key];
                const formattedValue = formatMonthlyValue(selectedMetric.key, value);
                const height = value === 0 ? 0 : Math.max((value / maximumMonthlyValue) * 100, 4);
                const isCurrentMonth = index === monthlyUsage.length - 1;

                return (
                  <ChartColumn key={month.label}>
                    <BarValue>{formattedValue}</BarValue>
                    <BarTrack>
                      {value > 0 && (
                        <Bar
                          role="img"
                          $accent={USAGE_METRIC_PRESENTATION[selectedMetric.key].accent}
                          $height={height}
                          $isCurrentMonth={isCurrentMonth}
                          aria-label={`${month.label}: ${formattedValue} ${selectedMetric.label.toLowerCase()}`}
                        />
                      )}
                    </BarTrack>
                    <MonthLabel $isCurrentMonth={isCurrentMonth}>{month.label}</MonthLabel>
                  </ChartColumn>
                );
              })}
            </ChartGrid>
          </ChartCard>

          {currentUsageMeters.length > 0 && (
            <CurrentPlanCard>
              <CurrentPlanHeader>
                <div>
                  <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
                    This billing cycle
                  </MDSText>
                  <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                    {currentPeriodLabel}
                  </MDSText>
                </div>
                <PlanPill>{planName}</PlanPill>
              </CurrentPlanHeader>

              <MeterList>
                {currentUsageMeters.map(meter => {
                  const percentage =
                    meter.allowance === null
                      ? 0
                      : Math.min((meter.used / meter.allowance) * 100, 100);
                  const unit = meter.unit ? ` ${meter.unit}` : "";
                  const used = meter.used.toLocaleString();
                  const allowance = meter.allowance?.toLocaleString() ?? "Unlimited";

                  return (
                    <MeterRow key={meter.label}>
                      <MeterLabelRow>
                        <MDSText variant={MDSTextVariant.TextXs}>{meter.label}</MDSText>
                        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                          {used}
                          {unit} of {allowance}
                          {meter.allowance === null ? "" : unit}
                        </MDSText>
                      </MeterLabelRow>
                      {meter.allowance !== null && (
                        <MeterTrack
                          role="progressbar"
                          aria-label={`${meter.label} usage`}
                          aria-valuemin={0}
                          aria-valuemax={meter.allowance}
                          aria-valuenow={meter.used}
                        >
                          <MeterFill $percentage={percentage} />
                        </MeterTrack>
                      )}
                    </MeterRow>
                  );
                })}
              </MeterList>
            </CurrentPlanCard>
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
}

const YearBadge = styled.div`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  line-height: 20px;
`;

const Introduction = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};

  @container mds-panel (max-width: 750px) {
    align-items: flex-start;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  @container mds-panel (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const accentColor = (theme: Theme, accent: "blue" | "gold" | "green" | "violet") => {
  switch (accent) {
    case "gold":
      return theme.colors.editor[300];
    case "green":
      return theme.colors.success;
    case "violet":
      return theme.colors.ink[200];
    default:
      return theme.colors.blueprint[300];
  }
};

const MetricCard = styled.button<{
  $accent: "blue" | "gold" | "green" | "violet";
  $isSelected: boolean;
}>`
  appearance: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $accent, $isSelected }) =>
      $isSelected ? accentColor(theme, $accent) : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.background.card.emphasis : theme.colors.surface.modal};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background-color 120ms ease,
    transform 120ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme, $accent }) => accentColor(theme, $accent)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;

const MetricCardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const MetricIcon = styled.div<{ $accent: "blue" | "gold" | "green" | "violet" }>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  color: ${({ theme, $accent }) => accentColor(theme, $accent)};
  background: ${({ theme }) => theme.colors.background.card.default};
`;

const PeriodPill = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
`;

const MetricValue = styled.span`
  margin-bottom: 2px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 28px;
`;

const ChartCard = styled.section`
  padding: ${({ theme }) => theme.spacing[5]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const ChartTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ChartTotal = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
`;

const ChartKey = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
`;

const ChartKeyDot = styled.span<{ $accent: "blue" | "gold" | "green" | "violet" }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme, $accent }) => accentColor(theme, $accent)};
`;

const ChartGrid = styled.div<{ $columnCount: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${({ $columnCount }) => $columnCount}, minmax(24px, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};
  height: 218px;
`;

const GridLine = styled.div<{ $position: number }>`
  position: absolute;
  z-index: 0;
  right: 0;
  left: 0;
  bottom: ${({ $position }) => 28 + $position * 80}px;
  border-top: 1px dashed ${({ theme }) => theme.colors.border.default};
`;

const ChartColumn = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-rows: 20px 1fr 20px;
  justify-items: center;
  min-width: 0;
`;

const BarValue = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
`;

const BarTrack = styled.div`
  display: flex;
  align-items: flex-end;
  width: min(30px, 70%);
  height: 100%;
`;

const Bar = styled.div<{
  $accent: "blue" | "gold" | "green" | "violet";
  $height: number;
  $isCurrentMonth: boolean;
}>`
  width: 100%;
  height: ${({ $height }) => $height}%;
  min-height: 4px;
  border-radius: 6px 6px 3px 3px;
  background: ${({ theme, $accent }) => accentColor(theme, $accent)};
  opacity: ${({ $isCurrentMonth }) => ($isCurrentMonth ? 0.5 : 1)};
  transition: height 220ms ease;
`;

const MonthLabel = styled.span<{ $isCurrentMonth: boolean }>`
  align-self: end;
  color: ${({ theme, $isCurrentMonth }) =>
    $isCurrentMonth ? theme.colors.text.primary : theme.colors.text.tertiary};
  font-size: 10px;
  font-weight: ${({ $isCurrentMonth }) => ($isCurrentMonth ? 600 : 400)};
`;

const CurrentPlanCard = styled.section`
  display: grid;
  grid-template-columns: minmax(150px, 0.75fr) minmax(260px, 1.25fr);
  gap: ${({ theme }) => theme.spacing[6]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.background.card.default};

  @container mds-panel (max-width: 750px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const CurrentPlanHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const PlanPill = styled.span`
  padding: 3px ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  color: ${({ theme }) => theme.colors.text.emphasis};
  background: ${({ theme }) => theme.colors.background.badge.emphasis};
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
`;

const MeterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const MeterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const MeterLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const MeterTrack = styled.div`
  height: 5px;
  overflow: hidden;
  border-radius: 99px;
  background: ${({ theme }) => theme.colors.border.default};
`;

const MeterFill = styled.div<{ $percentage: number }>`
  width: ${({ $percentage }) => $percentage}%;
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.blueprint[300]};
`;
